/// Leaderboard with infinite scroll + pinned current-player row.
/// Sticky personal position mirrors mobile `rank_page.dart`.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { useRankStore } from '@/rank/handler/rankStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';
import type { Ranking } from '@/user/domain/user';
import { formatScore } from '@/utils/formatScore';

const rowHeight = 48;
const listTopPadding = 16;
const listHorizontalInset = 24;
const loadMoreThreshold = 240;
const gold = '#FFC107';
const silver = '#B0BEC5';
const bronze = '#CD7F32';
/** Trophy icon slot; digit ranks use `ch` so multi-digit labels never clip. */
const trophySlotCh = 2.2;

type StickyEdge = 'none' | 'top' | 'bottom';

function rankSlotCh(maxRank: number): number {
  return Math.max(trophySlotCh, String(maxRank).length + 1);
}

function maxRankOnBoard(entries: Ranking[], myRank: Ranking | null): number {
  let max = 1;
  for (const entry of entries) {
    if (entry.rank > max) max = entry.rank;
  }
  if (myRank && myRank.rank > max) max = myRank.rank;
  return max;
}

export function RankPage() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const highScore = user.highScore;
  const currentUserId = user.isAnonymous ? '' : user.id;

  const board = useRankStore();
  const loadInitial = useRankStore((s) => s.loadInitial);
  const loadMore = useRankStore((s) => s.loadMore);

  const [scrolledUnder, setScrolledUnder] = useState(false);
  const [stickyEdge, setStickyEdge] = useState<StickyEdge>('none');
  const listRef = useRef<HTMLDivElement>(null);

  const syncStickyEdge = useCallback((): void => {
    const el = listRef.current;
    const stickyRank = board.myRank;
    if (!stickyRank || !currentUserId) {
      setStickyEdge('none');
      return;
    }

    const myIndex = board.entries.findIndex((entry) => entry.id === currentUserId);
    if (myIndex < 0) {
      // Rank known but row not in the loaded window — keep pinned at bottom.
      setStickyEdge('bottom');
      return;
    }

    if (!el) {
      setStickyEdge('none');
      return;
    }

    const rowTop = listTopPadding + myIndex * rowHeight;
    const rowBottom = rowTop + rowHeight;
    const viewTop = el.scrollTop;
    const viewBottom = el.scrollTop + el.clientHeight;

    if (rowBottom <= viewTop) setStickyEdge('top');
    else if (rowTop >= viewBottom) setStickyEdge('bottom');
    else setStickyEdge('none');
  }, [board.entries, board.myRank, currentUserId]);

  useEffect(() => {
    void loadInitial(false);
  }, [loadInitial, highScore, currentUserId]);

  useEffect(() => {
    // Layout must settle before measuring sticky vs viewport (mobile post-frame).
    const id = requestAnimationFrame(() => syncStickyEdge());
    return () => cancelAnimationFrame(id);
  }, [syncStickyEdge, board.entries.length, board.myRank]);

  const onScroll = (): void => {
    const el = listRef.current;
    if (!el) return;
    setScrolledUnder(el.scrollTop > 0.5);
    syncStickyEdge();
    if (!board.hasMore || board.loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - loadMoreThreshold) {
      void loadMore();
    }
  };

  const stickyRank = board.myRank;
  const rankWidthCh = rankSlotCh(maxRankOnBoard(board.entries, stickyRank));
  // Reserve pinned row (+ safe area) so the list doesn't jump under the pin.
  const bottomPad = stickyRank
    ? `calc(${rowHeight}px + env(safe-area-inset-bottom, 0px))`
    : currentUserId
      ? 24
      : 72;
  const showSticky = Boolean(stickyRank && stickyEdge !== 'none');

  return (
    <PageChrome title={t('rankTitle')} scrolledUnder={scrolledUnder}>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {board.loading && board.entries.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <span className="leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              …
            </span>
          </div>
        )}

        {board.error && board.entries.length === 0 && (
          <RankMessage
            message={board.error}
            actionLabel={t('rankRetry')}
            onAction={() => void loadInitial()}
          />
        )}

        {!board.loading && !board.error && board.entries.length === 0 && (
          <RankMessage message={t('rankEmpty')} />
        )}

        {board.entries.length > 0 && (
          <div
            ref={listRef}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-4"
            style={{ paddingBottom: bottomPad, touchAction: 'pan-y' }}
            onScroll={onScroll}
          >
            {board.entries.map((entry) => (
              <div key={entry.id} style={{ height: rowHeight }}>
                <RankRow
                  ranking={entry}
                  rankSlotCh={rankWidthCh}
                  highlighted={Boolean(currentUserId && entry.id === currentUserId)}
                />
              </div>
            ))}
            {(board.hasMore || board.loadingMore) && (
              <div className="flex items-center justify-center" style={{ height: rowHeight }}>
                <span className="leading-none" style={{ fontSize: 18, color: colors.onSurface, opacity: 0.5 }}>
                  …
                </span>
              </div>
            )}
            {board.error && board.entries.length > 0 && !board.loadingMore && (
              <div className="flex items-center justify-center gap-3" style={{ height: rowHeight }}>
                <button
                  type="button"
                  className="leading-none underline"
                  style={{ fontSize: 16, color: colors.onSurface }}
                  onClick={() => void loadMore()}
                >
                  {t('rankRetry')}
                </button>
              </div>
            )}
          </div>
        )}

        {showSticky && stickyRank && (
          <StickyRankCard ranking={stickyRank} rankSlotCh={rankWidthCh} edge={stickyEdge} />
        )}

        {!currentUserId && (
          <div
            className="absolute inset-x-6"
            style={{ bottom: `max(12px, env(safe-area-inset-bottom, 0px))` }}
          >
            <PixelContainer
              radius={2}
              pixelScale={2}
              padding="10px 14px"
              backgroundColor={colors.surfaceContainerHigh}
              borderColor={colors.onSurface}
              onClick={() => navigate(appRoutes.auth)}
            >
              <span className="block text-center leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
                {t('rankSignInHint')}
              </span>
            </PixelContainer>
          </div>
        )}
      </div>
    </PageChrome>
  );
}

function StickyRankCard({
  ranking,
  rankSlotCh: rankWidthCh,
  edge,
}: {
  ranking: Ranking;
  rankSlotCh: number;
  edge: StickyEdge;
}) {
  const colors = useThemeColors();
  // Full-bleed backdrop so list rows can't peek through edge gaps (mobile parity).
  return (
    <div
      className={`absolute inset-x-0 ${edge === 'top' ? 'top-0' : 'bottom-0'}`}
      style={{
        backgroundColor: colors.surface,
        paddingLeft: listHorizontalInset,
        paddingRight: listHorizontalInset,
        paddingBottom: edge === 'bottom' ? 'env(safe-area-inset-bottom, 0px)' : 0,
      }}
    >
      <div style={{ height: rowHeight }}>
        <RankRow ranking={ranking} rankSlotCh={rankWidthCh} highlighted />
      </div>
    </div>
  );
}

function RankRow({
  ranking,
  rankSlotCh: rankWidthCh,
  highlighted = false,
}: {
  ranking: Ranking;
  rankSlotCh: number;
  highlighted?: boolean;
}) {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const medal = ranking.rank === 1 ? gold : ranking.rank === 2 ? silver : ranking.rank === 3 ? bronze : null;
  const accent = highlighted ? gold : colors.onSurface;
  const background = highlighted ? colors.surfaceContainerHigh : colors.surfaceContainerHighest;
  const border = highlighted ? gold : colors.outline;

  return (
    <div className="h-full py-[3px]">
      <PixelContainer
        radius={2}
        pixelScale={2}
        padding="4px 12px"
        backgroundColor={background}
        borderColor={border}
      >
        <div className="flex h-[28px] min-w-0 items-center">
          <div className="shrink-0 text-right" style={{ width: `${rankWidthCh}ch` }}>
            {medal ? (
              <span className="inline-flex w-full justify-end">
                <PixelIcon name="trophy" set="pixel_icons" size={18} color={medal} />
              </span>
            ) : (
              <span className="leading-none" style={{ fontSize: 18, color: colors.onSurface, opacity: 0.65 }}>
                #{ranking.rank}
              </span>
            )}
          </div>
          <div className="ml-2.5 flex min-w-0 flex-1 items-center">
            <span className="min-w-0 flex-1 truncate leading-none" style={{ fontSize: 20, color: accent }}>
              {ranking.username}
            </span>
            {highlighted && (
              <span className="ml-1.5 shrink-0 leading-none" style={{ fontSize: 14, color: gold }}>
                ({t('rankYou')})
              </span>
            )}
          </div>
          <span className="ml-2 shrink-0 leading-none" style={{ fontSize: 20, color: highlighted ? gold : colors.onSurface }}>
            {formatScore(ranking.highScore)}
          </span>
        </div>
      </PixelContainer>
    </div>
  );
}

function RankMessage({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const colors = useThemeColors();
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <PixelContainer
        radius={3}
        pixelScale={2}
        padding="24px"
        backgroundColor={colors.surfaceContainerHighest}
        borderColor={colors.onSurface}
      >
        <div className="flex flex-col items-center">
          <PixelIcon name="trophy" set="pixel_icons" size={40} color={colors.onSurface} />
          <p className="mt-4 text-center leading-tight" style={{ fontSize: 20, color: colors.onSurface }}>
            {message}
          </p>
          {actionLabel && onAction && (
            <div className="mt-4">
              <PixelContainer
                radius={2}
                pixelScale={2}
                padding="10px 16px"
                backgroundColor={colors.onSurface}
                borderColor={colors.onSurface}
                onClick={onAction}
              >
                <span className="leading-none" style={{ fontSize: 18, color: colors.surface }}>
                  {actionLabel}
                </span>
              </PixelContainer>
            </div>
          )}
        </div>
      </PixelContainer>
    </div>
  );
}
